import { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { todayKey } from "./weekUtils";

// "Someone joined your game" alerts for the host. Watches the spots of the
// user's own open games and lists the confirmed joiners they haven't seen yet.
// What's been seen is remembered on this device only: { since, seen: [keys] }.
// Only joins after the first time this runs count, so nobody gets a flood of old ones.

function storeKey(uid) { return "gameJoinSeen_" + uid; }

function readStore(uid) {
  try {
    var raw = localStorage.getItem(storeKey(uid));
    if (raw) return JSON.parse(raw);
  } catch (e) { /* storage unavailable — fall through to a fresh start */ }
  var fresh = { since: new Date().toISOString(), seen: [] };
  writeStore(uid, fresh);
  return fresh;
}

function writeStore(uid, data) {
  try { localStorage.setItem(storeKey(uid), JSON.stringify(data)); } catch (e) { /* ignore */ }
}

export function useNewJoiners(user) {
  var uid = user ? user.uid : null;
  var [joiners, setJoiners] = useState([]); // every confirmed joiner across my open games
  var [store, setStore] = useState({ since: "", seen: [] });

  useEffect(function () {
    if (!uid) { setJoiners([]); return; }
    setStore(readStore(uid));
    var spotUnsubs = {};
    var perBooking = {};
    function publish() {
      var list = [];
      Object.keys(perBooking).forEach(function (id) { list = list.concat(perBooking[id]); });
      setJoiners(list);
    }
    var unsubBookings = onSnapshot(query(collection(db, "bookings"), where("uid", "==", uid)), function (snap) {
      var open = {};
      snap.docs.forEach(function (d) {
        var b = d.data();
        if (b.gameOpen && b.date >= todayKey()) open[d.id] = b;
      });
      Object.keys(spotUnsubs).forEach(function (id) {
        if (!open[id]) { spotUnsubs[id](); delete spotUnsubs[id]; delete perBooking[id]; }
      });
      Object.keys(open).forEach(function (id) {
        if (spotUnsubs[id]) return;
        var b = open[id];
        spotUnsubs[id] = onSnapshot(collection(db, "bookings", id, "spots"), function (s) {
          perBooking[id] = s.docs
            .filter(function (d) {
              var data = d.data();
              return data.uid !== uid && (data.status === undefined || data.status === "confirmed");
            })
            .map(function (d) {
              var data = d.data();
              return {
                key: id + "/" + d.id + "/" + data.uid,
                bookingId: id, name: data.displayName || "Someone", joinedAt: data.joinedAt || "",
                date: b.date, startMin: b.startMin, court: b.court,
              };
            });
          publish();
        }, function () { /* no access to this game's spots — nothing to alert about */ });
      });
      publish();
    }, function () { /* not allowed to list bookings (e.g. rules not published) — no alerts */ });
    return function () {
      unsubBookings();
      Object.keys(spotUnsubs).forEach(function (id) { spotUnsubs[id](); });
    };
  }, [uid]);

  var newJoiners = joiners.filter(function (j) {
    return store.seen.indexOf(j.key) === -1 && j.joinedAt > store.since;
  });

  function markSeen() {
    if (!uid) return;
    var next = { since: store.since, seen: store.seen.concat(newJoiners.map(function (j) { return j.key; })) };
    writeStore(uid, next);
    setStore(next);
  }

  return { newJoiners: newJoiners, markSeen: markSeen };
}
